import React, { useEffect, useState } from 'react';
import {
    Modal, Form, Input, Select, InputNumber, Row, Col,
    Switch, message, DatePicker, Alert
} from 'antd';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';

const { Option } = Select;
const { TextArea } = Input;

const AllowanceModal = ({ visible, onCancel, onSuccess, employeeId, editingRecord, dateOfJoining }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [calculationType, setCalculationType] = useState('fixed');
    const [allowanceTypes, setAllowanceTypes] = useState([]);

    // Disable dates before joining date
    const disabledDate = (current) => {
        if (!dateOfJoining) return false;
        return current && current < dayjs(dateOfJoining).startOf('day');
    };

    // Fetch allowance types from API
    useEffect(() => {
        const fetchTypes = async () => {
            const response = await adminactions.fetchAllowanceTypes();
            if (response) {
                // Handle both array and paginated response formats
                if (Array.isArray(response)) {
                    setAllowanceTypes(response);
                } else if (response.results) {
                    setAllowanceTypes(response.results);
                }
            }
        };
        fetchTypes();
    }, []);

    useEffect(() => {
        if (visible) {
            if (editingRecord && allowanceTypes.length > 0) {
                form.setFieldsValue({
                    ...editingRecord,
                    allowance_type: editingRecord.allowance_type?.id || editingRecord.allowance_type,
                    effective_date: editingRecord.effective_date ? dayjs(editingRecord.effective_date) : null,
                    end_date: editingRecord.end_date ? dayjs(editingRecord.end_date) : null
                });
                setCalculationType(editingRecord.calculation_type);
            } else if (!editingRecord) {
                form.resetFields();
                form.setFieldsValue({
                    calculation_type: 'fixed'
                });
                setCalculationType('fixed');
            }
        }
    }, [visible, editingRecord, form, allowanceTypes]);

    const handleCalculationTypeChange = (value) => {
        setCalculationType(value);
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const data = {
                employee: employeeId,
                effective_date: values.effective_date.format('YYYY-MM-DD'),
                allowance_type_id: values.allowance_type,
                allowance_name: values.allowance_name || null,
                calculation_type: values.calculation_type,
                amount: values.amount,
                end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
                is_attendance_based: values.is_attendance_based || false,
                notes: values.notes || null
            };

            if (editingRecord) {
                await adminactions.updateAllowance(editingRecord.id, data);
                message.success('Allowance updated successfully');
            } else {
                await adminactions.createAllowance(data);
                message.success('Allowance created successfully');
            }

            onSuccess();
            form.resetFields();
        } catch (error) {
            message.error('Failed to save allowance');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={editingRecord ? 'Edit Allowance' : 'Add Allowance'}
            open={visible}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            width={700}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    calculation_type: 'fixed'
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="effective_date"
                            label="Effective Date"
                            rules={[{ required: true, message: 'Please select effective date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="allowance_type"
                            label="Allowance Type"
                            rules={[{ required: true, message: 'Please select allowance type' }]}
                        >
                            <Select placeholder="Select type" loading={allowanceTypes.length === 0}>
                                {allowanceTypes.map(type => (
                                    <Option key={type.id} value={type.id}>
                                        {type.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="allowance_name"
                            label="Custom Name (Optional)"
                        >
                            <Input placeholder="Enter custom allowance name" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="calculation_type"
                            label="Calculation Type"
                            rules={[{ required: true, message: 'Please select calculation type' }]}
                        >
                            <Select onChange={handleCalculationTypeChange}>
                                <Option value="fixed">Fixed Amount</Option>
                                <Option value="percentage">Percentage of Basic Salary</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="amount"
                            label={calculationType === 'percentage' ? 'Percentage' : 'Amount'}
                            rules={[{ required: true, message: 'Please enter amount' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={calculationType === 'percentage' ? 100 : undefined}
                                step={calculationType === 'percentage' ? 0.1 : 100}
                                prefix={calculationType === 'fixed' ? '₹' : undefined}
                                suffix={calculationType === 'percentage' ? '%' : undefined}
                                placeholder={calculationType === 'percentage' ? 'e.g., 40' : 'Enter amount'}
                            />
                        </Form.Item>
                    </Col>

                    {calculationType === 'percentage' && (
                        <Col span={24}>
                            <Alert
                                message="Percentage-based Allowance"
                                description="The actual amount will be calculated during payroll based on the basic salary effective at that time."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        </Col>
                    )}

                    <Col span={12}>
                        <Form.Item
                            name="is_attendance_based"
                            label="Attendance Based"
                            valuePropName="checked"
                            tooltip="If enabled, this allowance will be prorated based on days present (including joining date)"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="end_date"
                            label="End Date (Optional)"
                            dependencies={['effective_date']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const startDate = getFieldValue('effective_date');
                                        if (!value || !startDate) {
                                            return Promise.resolve();
                                        }
                                        if (value.isBefore(startDate) || value.isSame(startDate, 'day')) {
                                            return Promise.reject(new Error('Start Date must be earlier than End Date'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="notes"
                            label="Notes"
                        >
                            <TextArea rows={3} placeholder="Enter any notes" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default AllowanceModal;
